import { useEffect, useRef, useState } from 'react';
import { trackPageView } from '@/lib/mixpanel';
import { 
  trackTimeOnPage, 
  trackScrollDepth, 
  trackPageInteraction,
  trackButtonInteraction,
  trackButtonClick as trackButtonClickFn,
  trackUserPath 
} from '@/lib/funnel-tracking';

interface UseEnhancedTrackingProps {
  pageName: string;
  userPath?: 'standard' | 'impact_flow' | 'external_application';
  trackScroll?: boolean;
  trackTime?: boolean;
  trackInteractions?: boolean;
  emitPageView?: boolean;
}

export const useEnhancedTracking = ({
  pageName,
  userPath = 'standard',
  trackScroll = true,
  trackTime = true,
  trackInteractions = true,
  emitPageView = true
}: UseEnhancedTrackingProps) => {
  const [scrollDepth, setScrollDepth] = useState(0);
  const [timeOnPage, setTimeOnPage] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [previousPage, setPreviousPage] = useState<string | null>(null);
  
  const startTime = useRef(Date.now());
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const timeInterval = useRef<NodeJS.Timeout | null>(null);

  // Track page view on mount
  useEffect(() => {
    const initialStartTime = startTime.current;
    // Only track page view if emitPageView is true
    if (emitPageView) {
      trackPageView(pageName, {
        page: pageName,
        user_path: userPath,
        timestamp: new Date().toISOString()
      });
    }

    // Track user path if we have a previous page
    if (previousPage) {
      trackUserPath(pageName, previousPage, pageName, userPath);
    }

    // Set up time tracking
    if (trackTime) {
      timeInterval.current = setInterval(() => {
        const currentTime = Date.now();
        const timeSpent = Math.floor((currentTime - startTime.current) / 1000);
        setTimeOnPage(timeSpent);
      }, 5000); // Changed from 1000ms to 5000ms (5 seconds) to reduce event frequency
    }

    // Cleanup on unmount
    return () => {
      if (timeInterval.current) {
        clearInterval(timeInterval.current);
      }
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      // Track final time on page
      if (trackTime) {
        const finalTime = Math.floor((Date.now() - initialStartTime) / 1000);
        if (finalTime > 5) { // Only track if user spent more than 5 seconds
          trackTimeOnPage(pageName, finalTime);
        }
      }
    };
  }, [pageName, userPath, trackTime, previousPage, emitPageView]);

  // Track scroll depth
  useEffect(() => {
    if (!trackScroll) {
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentScrollDepth = documentHeight > 0
        ? Math.round((scrollTop / documentHeight) * 100)
        : 0;
      
      setScrollDepth(currentScrollDepth);
      setIsScrolling(true);

      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      // Set new timeout to track scroll depth after user stops scrolling
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
        trackScrollDepth(pageName, currentScrollDepth);
      }, 1000);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageName, trackScroll]);

  // Track button clicks with enhanced analysis
  const trackButtonClickEnhanced = (buttonText: string, location: string, buttonType: 'cta' | 'navigation' | 'form' | 'cart' | 'external', additionalProps?: Record<string, unknown>) => {
    if (!trackInteractions) {return;}
    
    trackButtonClickFn(buttonText, location, pageName, buttonType, {
      user_path: userPath,
      scroll_depth: scrollDepth,
      time_on_page: timeOnPage,
      ...additionalProps
    });
  };

  // Legacy button click tracking for compatibility
  const trackButtonClick = (buttonText: string, location: string, additionalProps?: Record<string, unknown>) => {
    if (!trackInteractions) {return;}
    
    trackButtonInteraction(buttonText, location, pageName, {
      user_path: userPath,
      scroll_depth: scrollDepth,
      time_on_page: timeOnPage,
      ...additionalProps
    });
  };

  // Track page interactions
  const trackInteraction = (interaction: string, element: string, additionalProps?: Record<string, unknown>) => {
    if (!trackInteractions) {return;}
    
    trackPageInteraction(interaction, element, pageName, {
      user_path: userPath,
      scroll_depth: scrollDepth,
      time_on_page: timeOnPage,
      ...additionalProps
    });
  };

  // Track form interactions
  const trackForm = (formName: string, action: string, additionalProps?: Record<string, unknown>) => {
    if (!trackInteractions) {return;}
    
    trackPageInteraction(action, formName, pageName, {
      user_path: userPath,
      scroll_depth: scrollDepth,
      time_on_page: timeOnPage,
      form_name: formName,
      ...additionalProps
    });
  };

  // Track navigation
  const trackNavigation = (destination: string, method: 'click' | 'programmatic' = 'click') => {
    if (!trackInteractions) {return;}
    
    trackPageInteraction('navigation', destination, pageName, {
      user_path: userPath,
      scroll_depth: scrollDepth,
      time_on_page: timeOnPage,
      navigation_method: method,
      destination
    });
  };

  return {
    scrollDepth,
    timeOnPage,
    isScrolling,
    trackButtonClick,
    trackButtonClickEnhanced,
    trackInteraction,
    trackForm,
    trackNavigation,
    setPreviousPage
  };
};
